import type { ExpressionAst, JsonValue } from './functional-control-flow';
import { FORMULA_FUNCTIONS } from './functional-control-flow';

/**
 * Prints an expression tree as a formula, the way the backend's
 * `services/workflow_ir/formulas.py` does (`round(0.6 * rating + 0.4 * fit, 2)`,
 * `n % 15 == 0`). The backend is the only *parser*: the editor shows the
 * formula text, lets the user edit it, and sends it to
 * `POST /expressions/preview` to get the tree back. This printer only has to
 * produce text the backend reads back to the same tree.
 */

export const DEFAULT_ROOT = 'value';
const STANDARD_ROOTS = new Set([
  'value',
  'context',
  'judge',
  'attempt',
  'iteration',
]);
const COMPREHENSION_VARIABLES = new Set(['it', 'index']);
const COMPREHENSION_FUNCTIONS = new Set([
  'map',
  'filter',
  'find',
  'any',
  'all',
]);
const KEYWORDS = new Set(['and', 'or', 'not', 'in', 'true', 'false', 'null']);
const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;

const PRECEDENCE: Record<string, number> = {
  or: 1,
  and: 2,
  not: 3,
  eq: 4,
  ne: 4,
  lt: 4,
  lte: 4,
  gt: 4,
  gte: 4,
  in: 4,
  not_in: 4,
  concat: 5,
  add: 6,
  sub: 6,
  mul: 7,
  div: 7,
  mod: 7,
  neg: 8,
  pow: 9,
};
const ATOM = 10;
const COMPARISON_SYMBOL: Record<string, string> = {
  eq: '==',
  ne: '!=',
  lt: '<',
  lte: '<=',
  gt: '>',
  gte: '>=',
};
const ARITHMETIC_SYMBOL: Record<string, string> = {
  add: '+',
  sub: '-',
  mul: '*',
  div: '/',
  mod: '%',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const bareOk = (name: string): boolean =>
  IDENTIFIER.test(name) &&
  !KEYWORDS.has(name) &&
  !STANDARD_ROOTS.has(name) &&
  !COMPREHENSION_VARIABLES.has(name) &&
  !(name in FORMULA_FUNCTIONS) &&
  !COMPREHENSION_FUNCTIONS.has(name);

const key = (name: string): string =>
  IDENTIFIER.test(name) && !KEYWORDS.has(name) ? name : JSON.stringify(name);

const literal = (value: JsonValue): string => {
  if (value === null) return 'null';
  if (value === true) return 'true';
  if (value === false) return 'false';
  if (typeof value === 'number') return formatNumberLiteral(value);
  if (typeof value === 'string') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(literal).join(', ')}]`;
  return `{${Object.entries(value)
    .map(([k, v]) => `${key(k)}: ${literal(v)}`)
    .join(', ')}}`;
};

/**
 * Print a number the way Python's `repr` does, which is what the backend
 * printer emits: integers plain, exponent form (`1e-05`, `1.5e+16`, two-digit
 * exponent) when the decimal exponent is below -4 or at least 16.
 */
export const formatNumberLiteral = (value: number): string => {
  if (!Number.isFinite(value)) return 'null';
  if (Number.isInteger(value) && Math.abs(value) < 1e16) return String(value);
  if (value === 0) return '0.0';
  const exponent = Math.floor(Math.log10(Math.abs(value)));
  if (exponent < -4 || exponent >= 16) {
    const [mantissa, exp] = value.toExponential().split('e');
    const sign = exp.startsWith('-') ? '-' : '+';
    const digits = exp.replace(/^[-+]/, '').padStart(2, '0');
    return `${mantissa}e${sign}${digits}`;
  }
  return String(value);
};

export const formatPathAsFormula = (
  segments: ReadonlyArray<string | number>,
  root: string = DEFAULT_ROOT,
): string => {
  if (segments.length === 0) return root;
  const [first, ...rest0] = segments;
  let rest = rest0;
  let text: string;
  if (
    first === root &&
    rest.length > 0 &&
    typeof rest[0] === 'string' &&
    bareOk(rest[0])
  ) {
    text = rest[0];
    rest = rest.slice(1);
  } else {
    text = typeof first === 'string' ? first : `${root}[${first}]`;
  }
  for (const segment of rest) {
    if (typeof segment === 'number') text += `[${segment}]`;
    // After a dot any word is a field name, keywords included (`value.and`).
    else if (IDENTIFIER.test(segment)) text += `.${segment}`;
    else text += `[${JSON.stringify(segment)}]`;
  }
  return text;
};

const render = (node: ExpressionAst, parent: number, root: string): string => {
  const [text, precedence] = renderNode(node, root);
  return precedence < parent ? `(${text})` : text;
};

const renderNode = (node: ExpressionAst, root: string): [string, number] => {
  const r = (child: ExpressionAst, level: number) => render(child, level, root);
  switch (node.op) {
    case 'literal': {
      const negative = typeof node.value === 'number' && node.value < 0;
      return [literal(node.value), negative ? PRECEDENCE.neg : ATOM];
    }
    case 'path':
      return [formatPathAsFormula(node.path, root), ATOM];
    case 'exists':
      return [`exists(${formatPathAsFormula(node.path.path, root)})`, ATOM];
    case 'is_null':
      return [`is_null(${r(node.arg, 0)})`, ATOM];
    case 'not':
      return [`not ${r(node.arg, PRECEDENCE.not)}`, PRECEDENCE.not];
    case 'and':
    case 'or': {
      const level = PRECEDENCE[node.op];
      return [
        node.args.map((arg) => r(arg, level + 1)).join(` ${node.op} `),
        level,
      ];
    }
    case 'eq':
    case 'ne':
    case 'lt':
    case 'lte':
    case 'gt':
    case 'gte': {
      const level = PRECEDENCE[node.op];
      return [
        `${r(node.left, level + 1)} ${COMPARISON_SYMBOL[node.op]} ${r(node.right, level + 1)}`,
        level,
      ];
    }
    case 'in':
    case 'not_in': {
      const level = PRECEDENCE[node.op];
      return [
        `${r(node.item, level + 1)} ${node.op === 'in' ? 'in' : 'not in'} ${r(node.collection, level + 1)}`,
        level,
      ];
    }
    case 'concat': {
      const level = PRECEDENCE.concat;
      return [node.args.map((arg) => r(arg, level + 1)).join(' ~ '), level];
    }
    case 'add':
    case 'sub':
    case 'mul':
    case 'div':
    case 'mod': {
      const level = PRECEDENCE[node.op];
      return [
        `${r(node.left, level)} ${ARITHMETIC_SYMBOL[node.op]} ${r(node.right, level + 1)}`,
        level,
      ];
    }
    case 'pow': {
      const level = PRECEDENCE.pow;
      return [
        `${r(node.left, level + 1)} ^ ${r(node.right, PRECEDENCE.neg)}`,
        level,
      ];
    }
    case 'neg': {
      let inner: ExpressionAst = node.arg;
      let sign = -1;
      while (inner.op === 'neg') {
        inner = inner.arg;
        sign = -sign;
      }
      if (inner.op === 'literal' && typeof inner.value === 'number') {
        return renderNode({ op: 'literal', value: sign * inner.value }, root);
      }
      return [`-${r(node.arg, PRECEDENCE.neg)}`, PRECEDENCE.neg];
    }
    case 'index': {
      const base = r(node.base, ATOM);
      const idx = node.index;
      if (
        idx.op === 'literal' &&
        typeof idx.value === 'string' &&
        IDENTIFIER.test(idx.value)
      ) {
        return [`${base}.${idx.value}`, ATOM];
      }
      return [`${base}[${r(idx, 0)}]`, ATOM];
    }
    case 'call':
      return [
        `${node.fn}(${node.args.map((arg) => r(arg, 0)).join(', ')})`,
        ATOM,
      ];
    case 'map':
    case 'filter':
    case 'find':
    case 'any':
    case 'all':
      return [`${node.op}(${r(node.over, 0)}, ${r(node.body, 0)})`, ATOM];
    case 'object':
      return [
        `{${Object.entries(node.fields)
          .map(([k, child]) => `${key(k)}: ${r(child, 0)}`)
          .join(', ')}}`,
        ATOM,
      ];
    case 'list':
      return [`[${node.items.map((item) => r(item, 0)).join(', ')}]`, ATOM];
    default:
      throw new Error(
        `cannot print operator ${String((node as { op: unknown }).op)}`,
      );
  }
};

/** Print an expression as formula text; throws on an unknown operator. */
export const formatFormula = (
  expression: ExpressionAst,
  root: string = DEFAULT_ROOT,
): string => render(expression, 0, root);

/** `formatFormula` that returns null instead of throwing. */
export const tryFormatFormula = (
  expression: unknown,
  root: string = DEFAULT_ROOT,
): string | null => {
  if (!isRecord(expression) || typeof expression.op !== 'string') return null;
  try {
    return formatFormula(expression as ExpressionAst, root);
  } catch {
    return null;
  }
};

/** Every field path the expression reads (skipping `it` / `index`). */
export const expressionPaths = (
  expression: ExpressionAst,
): Array<Array<string | number>> => {
  const found: Array<Array<string | number>> = [];
  const seen = new Set<string>();
  const visit = (node: ExpressionAst) => {
    if (!isRecord(node)) return;
    let path: Array<string | number> | null = null;
    if (node.op === 'path') path = node.path;
    else if (node.op === 'exists') path = node.path.path;
    if (path && path[0] !== 'it' && path[0] !== 'index') {
      const k = JSON.stringify(path);
      if (!seen.has(k)) {
        seen.add(k);
        found.push(path);
      }
    }
    for (const child of childrenOf(node)) visit(child);
  };
  visit(expression);
  return found;
};

const childrenOf = (node: ExpressionAst): ExpressionAst[] => {
  switch (node.op) {
    case 'not':
    case 'is_null':
    case 'neg':
      return [node.arg];
    case 'and':
    case 'or':
    case 'concat':
    case 'call':
      return node.args;
    case 'eq':
    case 'ne':
    case 'lt':
    case 'lte':
    case 'gt':
    case 'gte':
    case 'add':
    case 'sub':
    case 'mul':
    case 'div':
    case 'mod':
    case 'pow':
      return [node.left, node.right];
    case 'in':
    case 'not_in':
      return [node.item, node.collection];
    case 'index':
      return [node.base, node.index];
    case 'map':
    case 'filter':
    case 'find':
    case 'any':
    case 'all':
      return [node.over, node.body];
    case 'object':
      return Object.values(node.fields);
    case 'list':
      return node.items;
    default:
      return [];
  }
};

/** The fields a formula reads, printed as the user would write them. */
export const formulaFields = (
  expression: ExpressionAst,
  root: string = DEFAULT_ROOT,
): string[] =>
  expressionPaths(expression).map((path) => formatPathAsFormula(path, root));
